import { getQueue, mutate, queuePartition, queueAuthorEmail } from './api'
import { toast } from './ui'

// A new job made with no signal (several jobs; Astra r1, finding 11). The job's creation waits in the save queue;
// its first visit cannot be booked until the job exists, because the visit names the job. So the visit waits here,
// on this phone, keyed by the queued creation, and is booked the moment that creation lands (api.ts announces it,
// even after a reload, because the queue sends it again under the same key and the server answers with the job).
// While one waits, the screens offer no second fresh creation for that customer.

const STORE = 'roundly:pending-job-visits'

// Kept per account, split exactly as the save queue is (Astra r2, finding 11): one account's look never clears
// another's waiting visit.
function storeKey(): string | null {
  const ns = queuePartition()
  return ns === null ? null : `${STORE}:${ns}`
}

export interface PendingJob {
  authorEmail?: string | null
  jobKey: string
  customerId: number
  jobName: string
  // The first visit's body, without job_id (added from the job the creation returns). Null: no visit to book.
  visit: Record<string, unknown> | null
}

function readAll(includeForeign = false): PendingJob[] {
  const key = storeKey()
  if (key === null) return []
  try {
    const raw = localStorage.getItem(key)
    const list = raw ? (JSON.parse(raw) as PendingJob[]) : []
    return Array.isArray(list) ? list.filter((p) => includeForeign || !p.authorEmail || p.authorEmail === queueAuthorEmail()) : []
  } catch {
    return []
  }
}

function writeAll(list: PendingJob[]): void {
  const key = storeKey()
  if (key === null) return
  const foreign = readAll(true).filter((p) => p.authorEmail && p.authorEmail !== queueAuthorEmail())
  list = [...foreign, ...list]
  try {
    if (list.length) localStorage.setItem(key, JSON.stringify(list))
    else localStorage.removeItem(key)
  } catch {
    // Storage refused: the job still saves from the queue; only its first visit then has to be booked by hand.
  }
}

export function keepPendingJob(p: PendingJob): void {
  writeAll([...readAll().filter((x) => x.jobKey !== p.jobKey), { ...p, authorEmail: queueAuthorEmail() }])
}

// The creation still waiting for this customer, if any. One whose save is no longer in the queue at all (refused,
// or dismissed by the owner) is forgotten.
export function pendingJobFor(customerId: number): PendingJob | null {
  const queued = new Set(getQueue().map((q) => q.key))
  const list = readAll()
  const live = list.filter((p) => queued.has(p.jobKey))
  if (live.length !== list.length) writeAll(live)
  return live.find((p) => p.customerId === customerId) ?? null
}

// The queued creation landed: book its first visit on the job it made.
export function onJobLanded(key: string, row: unknown): void {
  const p = readAll().find((x) => x.jobKey === key)
  if (!p) return
  writeAll(readAll().filter((x) => x.jobKey !== key))
  const id = (row as { id?: unknown } | null)?.id
  if (typeof id !== 'number' || !p.visit) return
  void mutate({
    label: `Book: ${p.jobName} first visit`,
    method: 'POST',
    path: `/api/customers/${p.customerId}/cleans`,
    body: { ...p.visit, job_id: id },
    jobsAware: true,
  }).then((ack) => {
    if (ack.ok) toast(`${p.jobName} saved and its first visit booked`)
    else if (ack.status >= 400 && ack.status < 500) toast(`${p.jobName} saved, but its first visit didn't book: ${ack.error ?? 'refused'}. Book it from their page.`)
  })
}

if (typeof window !== 'undefined') {
  window.addEventListener('roundly:queued-landed', (e) => {
    const d = (e as CustomEvent<{ key: string; row: unknown }>).detail
    if (d?.key) onJobLanded(d.key, d.row)
  })
}
